<template>
  <Transition name="anchored-drawer">
    <div
      v-if="isOpen"
      class="anchored-drawer"
      :class="`anchored-drawer--${edge}`"
      @keydown.escape="close"
    >
      <div class="anchored-drawer__scrim" @click="close" />
      <div
        :ref="bindPanel"
        class="anchored-drawer__panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? titleId : undefined"
        tabindex="-1"
      >
        <header v-if="title || $slots.header" class="anchored-drawer__head">
          <slot name="header" :close="close">
            <h2 :id="titleId" class="anchored-drawer__title">{{ title }}</h2>
          </slot>
        </header>
        <section class="anchored-drawer__body">
          <slot :close="close" />
        </section>
        <footer v-if="$slots.footer" class="anchored-drawer__foot">
          <slot name="footer" :close="close" />
        </footer>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { nextTick, useId } from 'vue';

// A drawer anchored to its CONTAINER, not the viewport: it slides in from one
// edge of whatever `position: relative` box it sits in, and its scrim dims that
// box and nothing else. PrimeVue's own Drawer is the viewport modal — it
// teleports to <body> and masks the whole page — which is the wrong tool when
// the thing being interrupted is one panel of a console, or one card on an
// identity screen, and the rest of the screen should stay readable.
//
// Slots: `header` (scoped: `{ close }`) replaces the default title; the default
// slot (scoped: `{ close }`) is the body; `footer` (scoped: `{ close }`) is
// where actions go. A drawer with neither `title` nor a header slot renders no
// head, and one with no footer slot renders no foot.
//
// Closing: a scrim click and Escape both close it — the model goes false AND
// `close` is emitted, so a consumer may drive it either way. It never opens
// itself; openness flows down.
//
// Focus: the panel takes focus on arrival (so Escape works from a keyboard
// without a click first, and the dialog announces its name) and hands it back
// to whatever opened it when it leaves. Both edges ride the function ref
// rather than the transition hooks, because a stubbed <Transition> — Vue Test
// Utils' default — never fires those hooks, and a consumer's test would then
// mount a drawer that is a dialog in markup and in nothing else.
defineOptions({ name: 'AnchoredDrawer' });

const isOpen = defineModel('open', { type: Boolean, default: false });

defineProps({
  // The heading, and what names the dialog (`aria-labelledby` points at it).
  title: { type: String, default: '' },
  // 'bottom' | 'right' | 'left' — the container edge the panel arrives from.
  edge: {
    type: String,
    default: 'bottom',
    validator: (value) => ['bottom', 'right', 'left'].includes(value),
  },
});

const emit = defineEmits(['close']);

const titleId = useId();

function close() {
  isOpen.value = false;
  emit('close');
}

let panelElement = null;
let opener = null;

// Vue re-invokes a function ref on every patch of its element, so the open
// branch runs once per arrival (guarded by identity) and the close branch once
// per departure (the ref is called with null).
function bindPanel(element) {
  if (element === panelElement) return;
  panelElement = element;
  if (element) {
    opener = document.activeElement;
    element.focus();
    // Under a stubbed <Transition> the element is not attached yet when the
    // ref fires; a tick later it is. focus() on a detached node is a no-op.
    if (document.activeElement !== element) {
      nextTick(() => {
        if (element.isConnected) element.focus();
      });
    }
    return;
  }
  if (opener?.isConnected) opener.focus();
  opener = null;
}
</script>

<style scoped>
/* The drawer fills its container so the scrim can dim all of it; the panel is
   then placed against one edge by the flex alignment below. Absolute, not
   fixed, is the whole point: the containing block is the consumer's. */
.anchored-drawer {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  overflow: hidden;
}

.anchored-drawer--bottom {
  align-items: flex-end;
  justify-content: center;
}

.anchored-drawer--right {
  justify-content: flex-end;
}

.anchored-drawer--left {
  justify-content: flex-start;
}

.anchored-drawer__scrim {
  position: absolute;
  inset: 0;
  background: var(--p-anchored-drawer-scrim-background);
}

.anchored-drawer__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  max-width: 100%;
  overflow: hidden;
  background: var(--p-anchored-drawer-panel-background);
  color: var(--p-anchored-drawer-panel-color);
  box-shadow: var(--p-anchored-drawer-panel-box-shadow);
  /* The panel holds focus on arrival but is not tabbable, so it has no
     keyboard affordance to lose by suppressing the ring. */
  outline: none;
}

.anchored-drawer--bottom .anchored-drawer__panel {
  width: 100%;
  border-top: var(--p-anchored-drawer-panel-border-width) solid var(--p-anchored-drawer-panel-border-color);
  border-radius: var(--p-anchored-drawer-panel-border-radius) var(--p-anchored-drawer-panel-border-radius) 0 0;
}

.anchored-drawer--right .anchored-drawer__panel {
  height: 100%;
  width: min(100%, 420px);
  border-left: var(--p-anchored-drawer-panel-border-width) solid var(--p-anchored-drawer-panel-border-color);
  border-radius: var(--p-anchored-drawer-panel-border-radius) 0 0 var(--p-anchored-drawer-panel-border-radius);
}

.anchored-drawer--left .anchored-drawer__panel {
  height: 100%;
  width: min(100%, 420px);
  border-right: var(--p-anchored-drawer-panel-border-width) solid var(--p-anchored-drawer-panel-border-color);
  border-radius: 0 var(--p-anchored-drawer-panel-border-radius) var(--p-anchored-drawer-panel-border-radius) 0;
}

.anchored-drawer__head {
  flex-shrink: 0;
  padding: var(--p-anchored-drawer-head-padding);
}

.anchored-drawer__title {
  margin: 0;
  font-family: var(--p-anchored-drawer-title-font-family);
  font-size: var(--p-anchored-drawer-title-font-size);
  font-weight: var(--p-anchored-drawer-title-font-weight);
  color: var(--p-anchored-drawer-title-color);
}

.anchored-drawer__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--p-anchored-drawer-body-padding);
}

.anchored-drawer__foot {
  flex-shrink: 0;
  padding: var(--p-anchored-drawer-foot-padding);
}

/* ── Transition ────────────────────────────────────────────────────── */

.anchored-drawer-enter-active .anchored-drawer__scrim,
.anchored-drawer-leave-active .anchored-drawer__scrim {
  transition: opacity 200ms ease;
}

.anchored-drawer-enter-active .anchored-drawer__panel,
.anchored-drawer-leave-active .anchored-drawer__panel {
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);
}

.anchored-drawer-enter-from .anchored-drawer__scrim,
.anchored-drawer-leave-to .anchored-drawer__scrim {
  opacity: 0;
}

.anchored-drawer--bottom.anchored-drawer-enter-from .anchored-drawer__panel,
.anchored-drawer--bottom.anchored-drawer-leave-to .anchored-drawer__panel {
  transform: translateY(100%);
}

.anchored-drawer--right.anchored-drawer-enter-from .anchored-drawer__panel,
.anchored-drawer--right.anchored-drawer-leave-to .anchored-drawer__panel {
  transform: translateX(100%);
}

.anchored-drawer--left.anchored-drawer-enter-from .anchored-drawer__panel,
.anchored-drawer--left.anchored-drawer-leave-to .anchored-drawer__panel {
  transform: translateX(-100%);
}
</style>
